import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Import components
import LoginPage from "./components/LoginPage";
import RegistrationPage from "./components/RegistrationPage";
import AdminDashboard from "./components/AdminDashboard";
import UserDashboardNew from "./components/UserDashboardNew";
import SuperAdminLogin from "./components/SuperAdminLogin";
import SuperAdminDashboard from "./components/SuperAdminDashboard";
import Navbar from "./components/Navbar";

// Import services
import { authService } from "./services/authService";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const userData = await authService.getCurrentUser();
          setUser(userData);
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        localStorage.removeItem("token");
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem("token", token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  useEffect(() => {
    const HEALTH_URL = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL + "/health"
      : "https://dailybackend-zvvt.onrender.com/health";

    const pingBackend = async () => {
      try {
        const res = await fetch(HEALTH_URL);
        if (res.ok) {
          console.log("[Health Check] Backend is healthy");
        } else {
          console.warn("[Health Check] Backend health check failed");
        }
      } catch (err) {
        console.error("[Health Check] Error pinging backend:", err);
      }
    };

    // Initial ping
    pingBackend();
    // Ping every 5 minutes
    const interval = setInterval(pingBackend, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="App">
      {user && <Navbar user={user} onLogout={handleLogout} />}

      <Routes>
        <Route
          path="/login"
          element={
            user ? (
              <Navigate
                to={
                  user.role === "superadmin"
                    ? "/superadmin"
                    : user.role === "admin" || user.accountType === "business"
                    ? "/admin"
                    : "/dashboard"
                }
              />
            ) : (
              <LoginPage onLogin={handleLogin} />
            )
          }
        />

        <Route
          path="/superadmin"
          element={
            user && user.role === "superadmin" ? (
              <SuperAdminDashboard user={user} />
            ) : user ? (
              <Navigate
                to={
                  user.role === "admin" || user.accountType === "business"
                    ? "/admin"
                    : "/dashboard"
                }
              />
            ) : (
              <SuperAdminLogin onLogin={handleLogin} />
            )
          }
        />

        <Route
          path="/register"
          element={
            user ? (
              <Navigate
                to={
                  user.role === "superadmin"
                    ? "/superadmin"
                    : user.role === "admin" || user.accountType === "business"
                    ? "/admin"
                    : "/dashboard"
                }
              />
            ) : (
              <RegistrationPage
                onRegistrationSuccess={() => (window.location.href = "/login")}
              />
            )
          }
        />

        <Route
          path="/admin"
          element={
            user &&
            (user.role === "admin" || user.accountType === "business") ? (
              <AdminDashboard user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        <Route
          path="/dashboard"
          element={
            user &&
            (user.role === "user" || user.accountType === "regular_user") ? (
              <UserDashboardNew user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        <Route
          path="/"
          element={
            user ? (
              <Navigate
                to={
                  user.role === "superadmin"
                    ? "/superadmin"
                    : user.role === "admin" || user.accountType === "business"
                    ? "/admin"
                    : "/dashboard"
                }
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <SpeedInsights />
    </div>
  );
}

export default App;
